import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const line1 = "DISKO";
const line2 = "BHUTE.";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.3 },
  },
};
const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04 } },
};
const letter = {
  hidden: { y: "110%", opacity: 0 },
  show: {
    y: "0%",
    opacity: 1,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};
const tagFade = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } },
};

function AnimatedLine({ text, delay = 0 }) {
  return (
    <motion.span
      variants={lineWrap}
      initial="hidden"
      animate="show"
      transition={{ delayChildren: delay }}
      className="block overflow-hidden"
    >
      {text.split("").map((ch, i) => (
        <motion.span
          key={i}
          variants={letter}
          className="inline-block"
          style={{ willChange: "transform" }}
        >
          {ch === "." ? <span className="text-[#FF2A2A]">{ch}</span> : ch}
        </motion.span>
      ))}
    </motion.span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const devY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  const [time, setTime] = useState("");
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const hh = String(d.getUTCHours()).padStart(2, "0");
      const mm = String(d.getUTCMinutes()).padStart(2, "0");
      setTime(`${hh}:${mm} UTC`);
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const scrollDown = () => {
    document.getElementById("bio")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex flex-col justify-end"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 bg-cover bg-center will-change-transform"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1701705319364-ec5468034cf6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxkaiUyMHBsYXlpbmclMjBsaXZlJTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc3ODQ5ODE3MHww&ixlib=rb-4.1.0&q=85)",
          }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#050505]/70" />
      <div className="absolute inset-0 hero-fade-mask" />

      {/* Devanagari overlay with subtle parallax */}
      <motion.div
        style={{ y: devY }}
        className="devanagari-overlay pointer-events-none select-none absolute -bottom-10 left-0 right-0 text-[18vw] leading-[0.85] opacity-[0.07] whitespace-nowrap overflow-hidden"
      >
        डिस्को&nbsp;भूते
      </motion.div>

      {/* Top-left HUD */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute top-24 left-6 sm:left-8 z-10 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F5F5F5]/50 flex flex-col gap-1"
      >
        <span>REC · 001</span>
        <span className="text-[#FF2A2A]">{time}</span>
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-7xl w-full mx-auto px-6 sm:px-8 pb-20 sm:pb-28 pt-32"
      >
        <motion.div
          variants={tagFade}
          initial="hidden"
          animate="show"
          transition={{ delay: 1.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="block w-10 h-px bg-[#FF2A2A]" />
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#F5F5F5]/80">
            Berlin · Bangalore · Everywhere After Midnight
          </span>
        </motion.div>

        <motion.h1
          data-testid="hero-title"
          variants={container}
          initial="hidden"
          animate="show"
          className="font-heading font-black tracking-tighter uppercase text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.85]"
        >
          <AnimatedLine text={line1} />
          <AnimatedLine text={line2} delay={0.2} />
        </motion.h1>

        <motion.p
          data-testid="hero-tagline"
          variants={tagFade}
          initial="hidden"
          animate="show"
          transition={{ delay: 1.4 }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#A8A8A8]"
        >
          A ghost on the decks. Dark minimal techno and psy tech — hypnotic loops,
          machine rituals, and low-end ceremonies. The midnight sound, engineered for
          rooms where the walls forget your name.
        </motion.p>

        <motion.div
          variants={tagFade}
          initial="hidden"
          animate="show"
          transition={{ delay: 1.6 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-book"
            onClick={() =>
              document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })
            }
            className="group relative overflow-hidden font-mono text-xs tracking-[0.25em] uppercase bg-[#FF2A2A] text-black px-8 py-4 transition-colors hover:text-black"
          >
            <span className="relative z-10">Book a Set →</span>
            <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
          </button>
          <button
            data-testid="hero-cta-mixes"
            onClick={() =>
              document.getElementById("mixes")?.scrollIntoView({ behavior: "smooth" })
            }
            className="group relative font-mono text-xs tracking-[0.25em] uppercase border border-white/30 text-white px-8 py-4 hover:border-[#FF2A2A] hover:text-[#FF2A2A] transition-colors"
          >
            Listen / Mixes
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        data-testid="hero-scroll-down"
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        className="absolute bottom-6 right-6 sm:bottom-10 sm:right-10 z-10 flex flex-col items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F5F5F5]/60 hover:text-[#FF2A2A] transition-colors"
        aria-label="Scroll down"
      >
        <span>Scroll</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
